/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    makeStyles,
    useMediaQuery,
} from '@material-ui/core';
import { useUIDSeed } from 'react-uid';
import {
    arrayOf, func, shape, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import mbpLogger from '@module-logger/mbp';

import { useQuery } from '@apollo/client';
import { setCategoryTabs } from '../../../../../state/ducks/App/App-Actions';
import noop from '../../../../helpers/noop';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getSalesforceAttributes } from '../../../../../state/ducks/App/App-Selectors';
import { NavigationPillsSkeleton } from '../../GraphqlSkeletonComponents/TabTrendingProductSkeleton';
import { getBrandName } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import findContentByUID from '../../../../gql/queries/findContentByUID';
import {
    BANNER_IMPRESSION_EVENT,
    BANNER_INTERACTION_EVENT,
    buildTrendingTabEvent,
} from './trendingTabsTracking';
import { setActiveBannerProps } from '../../../../helpers/tracking/banner/activeBannerHelpers';

const useStyles = makeStyles((theme) => ({
    root: (styles) => ({
        maxWidth: styles.maxWidth || '',
        margin: styles?.margin || '5px auto 20px',
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: '0 10px',
        [theme.breakpoints.up(1024)]: {
            padding: '0 20px',
        },
        [theme.breakpoints.up(1400)]: {
            padding: '0',
        },
    }),
    wrapper: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        [theme.breakpoints.down(599)]: {
            justifyContent: 'flex-start',
        },
    },
    scrollViewport: (styles) => ({
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
        scrollbarWidth: 'none',
        width: styles?.hasControls ? 'calc(100% - 96px)' : 'auto',
        maxWidth: '100%',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        [theme.breakpoints.down(599)]: {
            width: '100%',
        },
    }),
    tabsTrack: (styles) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: '100%',
        justifyContent: styles?.centerDesktop ? 'center' : 'flex-start',
        paddingBottom: '2px',
        [theme.breakpoints.down(599)]: {
            justifyContent: 'flex-start',
        },
    }),
    tab: (styles) => ({
        minHeight: '45px',
        padding: '6px 20px',
        borderRadius: '37px',
        border: `1px solid ${styles?.border_color?.color || '#DDDDDD'}`,
        backgroundColor: '#FFFFFF',
        color: styles?.color?.color || '#636464',
        fontSize: styles?.font_size || '16px',
        fontFamily: styles?.font_family || 'inherit',
        fontWeight: styles?.font_weight || '300',
        lineHeight: '1',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        transition: 'background-color .2s ease, border-color .2s ease, color .2s ease',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        outline: 'none',
        boxShadow: 'none',
        appearance: 'none',
        '&:hover': {
            backgroundColor: '#E7EDF1',
            borderColor: styles?.border_color?.color || '#DDDDDD',
            color: styles?.hover_color?.color || styles?.color?.color || '#636464',
        },
        '&:focus-visible': {
            boxShadow: '0 0 0 2px #2f2f2f',
        },
    }),
    active: (styles) => ({
        backgroundColor: styles?.hover_color?.color || '#2f2f2f',
        borderColor: styles?.hover_color?.color || '#2f2f2f',
        color: '#FFFFFF',
        fontWeight: '500',
        '&:hover': {
            backgroundColor: styles?.hover_color?.color || '#2f2f2f',
            borderColor: styles?.hover_color?.color || '#2f2f2f',
            color: '#FFFFFF',
        },
    }),
    controls: {
        display: 'flex',
        gap: '8px',
        flexShrink: 0,
    },
    button: (styles) => ({
        width: '32px',
        height: '32px',
        borderRadius: '999px',
        border: 'none',
        backgroundColor: '#FFFFFF',
        color: styles?.hover_color?.color || '#2f2f2f',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.16)',
        outline: 'none',
        appearance: 'none',
        flexShrink: 0,
    }),
    chevron: (styles) => ({
        width: '8px',
        height: '8px',
        borderRight: `1.5px solid ${styles?.hover_color?.color || '#2f2f2f'}`,
        borderBottom: `1.5px solid ${styles?.hover_color?.color || '#2f2f2f'}`,
        display: 'inline-block',
    }),
    chevronLeft: {
        transform: 'rotate(135deg)',
        marginLeft: '3px',
    },
    chevronRight: {
        transform: 'rotate(-45deg)',
        marginRight: '3px',
    },
    disabledControl: {
        opacity: '0.45',
        cursor: 'default',
        pointerEvents: 'none',
    },
}));

export const CategoryTabs = ({ maxWidth, categoryTabs, trackClickThrough }) => {
    const categoryStyles = categoryTabs?.styles || {};
    const dispatch = useDispatch();
    const seed = useUIDSeed();
    const scrollRef = useRef(null);
    const [categoryIndex, setCategoryIndex] = useState(Math.max((categoryTabs?.default_selected_index || 0) - 1, -1));
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 600px)');
    const salesforceAttributes = useSelector(getSalesforceAttributes);

    useEffect(() => {
        categoryTabs?.categories?.forEach((category) => {
            dispatch(trackEvent(buildTrendingTabEvent({
                eventName: BANNER_IMPRESSION_EVENT,
                bannerGroup: 'Trending Tabs Impression',
                category,
            })));
        });
    }, [categoryTabs, dispatch]);

    const classes = useStyles({
        maxWidth,
        ...categoryStyles,
        hasControls: isDesktop && (canScrollNext || canScrollPrev),
        centerDesktop: isDesktop && !(canScrollNext || canScrollPrev),
    });

    useEffect(() => {
        if (salesforceAttributes?.categoryTabs?.referenceProductCategory) {
            const idx = categoryTabs?.categories?.findIndex(
                (ele) => ele?.key === salesforceAttributes.categoryTabs.referenceProductCategory,
            );
            setCategoryIndex(idx);
        } else {
            setCategoryIndex(Math.max((categoryTabs?.default_selected_index || 0) - 1, -1));
        }
    }, [categoryTabs, salesforceAttributes?.categoryTabs]);

    useEffect(() => {
        const updateScrollButtons = () => {
            const el = scrollRef.current;
            if (!el) return;
            const maxScrollLeft = el.scrollWidth - el.clientWidth;
            setCanScrollPrev(el.scrollLeft > 0);
            setCanScrollNext(maxScrollLeft > 1 && el.scrollLeft < maxScrollLeft - 1);
        };

        updateScrollButtons();
        const currentRef = scrollRef.current;
        currentRef?.addEventListener('scroll', updateScrollButtons);
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateScrollButtons);
        }

        return () => {
            currentRef?.removeEventListener('scroll', updateScrollButtons);
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', updateScrollButtons);
            }
        };
    }, [categoryTabs, isDesktop]);

    const handleClick = (index, tabTitle) => {
        const category = categoryTabs?.categories[index];
        const bannerGroup = categoryTabs?.tracking?.feature_element || 'Trending Gifts';
        const bannerItemName = category?.title || tabTitle || '';

        setActiveBannerProps('Trending Tab', bannerGroup, bannerItemName);

        dispatch(trackEvent(buildTrendingTabEvent({
            eventName: BANNER_INTERACTION_EVENT,
            bannerGroup: categoryTabs?.tracking?.feature_element || 'Trending Tabs Click',
            category,
        })));

        if (categoryIndex === index) {
            return;
        }

        trackClickThrough(category?.category_id ? category?.category_id : category?.key, tabTitle, categoryTabs?.tracking);

        dispatch(
            setCategoryTabs({
                referenceProductID: category?.product_id,
                referenceProductCategory: category?.key,
                categoryId: category?.category_id,
            }),
        );
        setCategoryIndex(index);
    };

    const handleKeyDown = (event, index, tabTitle) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick(index, tabTitle);
        }
    };

    const handlePrev = () => {
        scrollRef.current?.scrollBy({ left: -240, behavior: 'smooth' });
    };

    const handleNext = () => {
        scrollRef.current?.scrollBy({ left: 240, behavior: 'smooth' });
    };

    if (categoryTabs?.categories?.length > 0) {
        const showControls = isDesktop && (canScrollNext || canScrollPrev);

        return (
            <div className={classes.root}>
                <div className={classes.wrapper}>
                    {showControls && (
                        <div className={classes.controls}>
                            <button
                                className={`${classes.button} ${!canScrollPrev ? classes.disabledControl : ''}`}
                                type="button"
                                onClick={handlePrev}
                                disabled={!canScrollPrev}
                                aria-label="Scroll tabs left"
                            >
                                <span className={`${classes.chevron} ${classes.chevronLeft}`} />
                            </button>
                        </div>
                    )}
                    <div className={classes.scrollViewport} ref={scrollRef}>
                        <div className={classes.tabsTrack}>
                            {categoryTabs.categories.map((item, index) => (
                                <button
                                    key={seed(item)}
                                    className={`${classes.tab} ${categoryIndex === index ? classes.active : ''}`}
                                    type="button"
                                    onKeyDown={(event) => handleKeyDown(event, index, item?.title)}
                                    onClick={() => handleClick(index, item?.title)}
                                    aria-pressed={categoryIndex === index}
                                >
                                    {item?.title}
                                </button>
                            ))}
                        </div>
                    </div>
                    {showControls && (
                        <div className={classes.controls}>
                            <button
                                className={`${classes.button} ${!canScrollNext ? classes.disabledControl : ''}`}
                                type="button"
                                onClick={handleNext}
                                disabled={!canScrollNext}
                                aria-label="Scroll tabs right"
                            >
                                <span className={`${classes.chevron} ${classes.chevronRight}`} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return null;
};

CategoryTabs.propTypes = {
    maxWidth: string,
    categoryTabs: shape({
        categories: arrayOf(
            shape({
                label: string,
            }),
        ),
    }),
    trackClickThrough: func,
};

CategoryTabs.defaultProps = {
    maxWidth: '',
    categoryTabs: { categories: [] },
    trackClickThrough: noop,
};

const NavigationPillsContainerV2 = ({
    navPillsUID, maxWidth,
}) => {
    if (!navPillsUID) return null;
    const dispatch = useDispatch();

    const brandName = useSelector(getBrandName);

    const { loading, error, data } = useQuery(
        findContentByUID({
            queryName: 'NavigationPills',
            brand: brandName,
            uid: navPillsUID?.uid,
            contentType: navPillsUID?._content_type_uid,
        }),
        {
            skip: !navPillsUID?.uid,
            fetchPolicy: 'cache-first',
        },
    );

    if (loading) {
        return <NavigationPillsSkeleton />;
    }

    if (error) {
        mbpLogger.logError({
            function: `NavigationPillsContainerV2 - navPillsUID = ${navPillsUID}`,
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'NavigationPillsContainerV2',
            jsError: error,
        });
        return null;
    }

    const handleClick = (category, tabTitle, tracking) => {
        dispatch(emitCustomTrackingEvent({
            action: 'Click',
            category,
            page: { type: 'home' },
            eventSubType: 'feature-tracking',
        }));
        dispatch(trackEvent({
            eventCategory: tracking?.feature_category || 'Homepage',
            eventAction: tracking?.feature_element || 'Trending Tabs Click',
            eventLabel: tabTitle?.split(' ')?.join('-')?.toLowerCase(),
        }));
    };

    if (data?.findContentByUID?.content?.entry) {
        return (
            <CategoryTabs
                maxWidth={maxWidth}
                categoryTabs={data?.findContentByUID?.content?.entry}
                trackClickThrough={handleClick}
            />
        );
    }

    return null;
};

NavigationPillsContainerV2.propTypes = {
    maxWidth: string,
    navPillsUID: string,
};

NavigationPillsContainerV2.defaultProps = {
    maxWidth: '',
    navPillsUID: '',
};

export default NavigationPillsContainerV2;
